import { Component, DestroyRef, effect, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {MatPaginator, MatPaginatorModule} from '@angular/material/paginator';
import {MatSort, MatSortModule} from '@angular/material/sort';
import { MatTableModule} from '@angular/material/table';
import {  MatInputModule } from '@angular/material/input';
import {MatFormFieldModule} from '@angular/material/form-field';
import { Category } from '@shared/interfacres/category';
import { Genre } from '@shared/interfacres/genre';
import { DatePipe } from '@angular/common';
import { MatIcon } from '@angular/material/icon';

import { debounceTime, firstValueFrom } from 'rxjs';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';

import { MatDialog } from '@angular/material/dialog';

import { MatProgressBar } from '@angular/material/progress-bar';
import { MatCheckbox } from '@angular/material/checkbox';

import {SelectionModel} from '@angular/cdk/collections';
import { ConfirmationDialog } from '@shared/components/confirmation-dialog/confirmation-dialog';
import { CategoriesManagerStore } from '@data-access/manager/stores/categories-manager.store';
import { EditCategoryDialog } from './edit-category-dialog/edit-category-dialog';
import { GenresManagerStore } from '@data-access/manager/stores/genres-manager.store';
import { IconInput } from '@shared/components/icon-input/icon-input';


@Component({
  selector: 'app-categories',
  templateUrl: './categories.html',
  styleUrl: './categories.scss',

  imports: [MatFormFieldModule, MatInputModule, MatTableModule, MatSortModule, MatPaginatorModule,
    DatePipe, MatIcon, ReactiveFormsModule, MatProgressBar, MatCheckbox, MatButton, IconInput],
})
export class Categories implements OnInit, OnDestroy {
  displayedColumns: string[] = ['id', 'name', 'updated_at', 'actions'];
  dataSource: Genre[] = [];
  total = 0;

  searchValue = new FormControl('');
  readonly genreStore = inject(GenresManagerStore);

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  selection = new SelectionModel<number>(
    true,
    []
  );
  readonly store = inject(CategoriesManagerStore);
  private destroyRef = inject(DestroyRef);
  readonly dialog = inject(MatDialog);

  constructor(
  ) {

    effect(() => {
      this.dataSource = this.store.items();
      this.total = this.store.total();
    });
  }

  ngOnInit(): void {
    this.genreStore.getAll();
    this.searchValue.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(400),
      )
      .subscribe(input => {
        this.store.getByName(input as string);
      })
    this.store.setFilter('updated_at', 'desc');
  }

  changePage(page: {pageIndex: number; pageSize: number}) {
    this.store.setPagination(page.pageIndex + 1, page.pageSize);
  }

  sortChange(event: {active: string; direction: string}) {
    this.store.setFilter(event.active, event.direction as 'asc' | 'desc');

  }

  async edit(item: Category) {
    const dialogRef = this.dialog.open(EditCategoryDialog, {
      data: {
        category: item,
        genresList: this.genreStore.fullList()
      },
      disableClose: true,
      width: '70%',
      maxWidth: '900px',
      panelClass: 'admin-dialog',
      backdropClass: 'admin-dialog-backdrop'
    });

    const result = await firstValueFrom(dialogRef.afterClosed());
    if (result) {
      const category = {
        name: result.name,
        genreIds: result.genres.map((genre: Genre) => genre.id)
      }
      this.store.update(item.id, category);
    }
  }

  async add() {
    const dialogRef = this.dialog.open(EditCategoryDialog, {
      data: {
        genresList: this.genreStore.fullList()
      },
      disableClose: true,
      width: '700px',
      panelClass: 'admin-dialog',
      backdropClass: 'admin-dialog-backdrop'
    });

    const result = await firstValueFrom(dialogRef.afterClosed());
    if (result) {
      const category = {
        name: result.name,
        genreIds: result.genres.map((genre: Genre) => genre.id)
      }
      this.store.create(category);
    }
  }

  async bulkDelete() {
    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {
        title: 'Delete selected',
        message: 'Are you sure you want to delete selected categories?'
      },
      disableClose: true,
      panelClass: 'admin-dialog',
      backdropClass: 'admin-dialog-backdrop'
    });

    const result = await firstValueFrom(dialogRef.afterClosed());

    if (result) {
      this.store.delete(this.selection.selected);
      this.selection.clear();
    }

  }

  ngOnDestroy() {
    this.store.clearCriteria();
  }
}
